'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
    $getSelection,
    $isRangeSelection,
    $createTextNode,
    COMMAND_PRIORITY_HIGH,
    KEY_ESCAPE_COMMAND,
} from 'lexical'
import { $createMentionNode } from './MentionNode'
import MentionDropdown from './MentionDropdown'

/**
 * Artifact-mention autocomplete: watches for a single-character `trigger`
 * (`#` for character artifacts, `@` for locations, `&` for props, `/` for
 * every other scope) at a word boundary,
 * shows a filtered dropdown of `items`, and inserts the chosen one as an inline
 * `MentionNode` of `mentionType` — `artifact`, which serializes to
 * `{{"type":"artifact","artifactId":…}}`.
 *
 * Unlike the script editor's CharacterMentionPlugin (which pushes mentions
 * onto their own line for the "one character per line" script layout), this
 * keeps the chip inline with the surrounding text — the right behaviour for a
 * free-form prompt field.
 *
 * `items` are pre-mapped `{ id, label, description, data }`; `icon` is an
 * optional React element rendered on every row (per-item `item.icon` wins).
 */
function getQueryAndRange(editor, trigger) {
    return editor.getEditorState().read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null

        const anchor = selection.anchor
        if (anchor.type !== 'text') return null

        const anchorNode = anchor.getNode()
        const text = anchorNode.getTextContent().slice(0, anchor.offset)

        const triggerIndex = text.lastIndexOf(trigger)
        if (triggerIndex === -1) return null

        const charBefore = triggerIndex > 0 ? text[triggerIndex - 1] : ' '
        if (charBefore !== ' ' && charBefore !== '\n' && triggerIndex !== 0) return null

        const query = text.slice(triggerIndex + 1)
        // Names can contain spaces (prompts especially), so allow them but bail
        // once the run gets long enough that the user has clearly moved on.
        if (query.includes('\n') || query.length > 40) return null

        return { query, triggerIndex, anchorOffset: anchor.offset, anchorKey: anchor.key }
    })
}

export default function ArtifactMentionPlugin({
    trigger = '/',
    mentionType = 'command',
    items = [],
    icon = null,
    emptyMessage = 'No results',
}) {
    const [editor] = useLexicalComposerContext()
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [anchorRect, setAnchorRect] = useState(null)
    const matchRef = useRef(null)

    const filteredItems = items
        .filter(item => (item.label || '').toLowerCase().includes(query.toLowerCase()))
        .map(item => ({ ...item, icon: item.icon || icon }))

    const updateDropdownPosition = useCallback(() => {
        const domSelection = window.getSelection()
        if (!domSelection || domSelection.rangeCount === 0) return
        const range = domSelection.getRangeAt(0)
        setAnchorRect(range.getBoundingClientRect())
    }, [])

    const close = useCallback(() => {
        setIsOpen(false)
        setQuery('')
        matchRef.current = null
    }, [])

    const handleSelect = useCallback((item) => {
        editor.update(() => {
            const match = matchRef.current
            if (!match) return

            const selection = $getSelection()
            if (!$isRangeSelection(selection)) return

            const anchorNode = selection.anchor.getNode()
            const text = anchorNode.getTextContent()

            const before = text.slice(0, match.triggerIndex)
            const after = text.slice(match.anchorOffset)

            anchorNode.setTextContent(before)

            const mentionNode = $createMentionNode(
                mentionType,
                item.id,
                item.label,
                item.data || {},
            )

            if (before.length > 0) {
                anchorNode.insertAfter(mentionNode)
            } else {
                anchorNode.replace(mentionNode)
            }

            // A text node after the chip gives the caret somewhere to land and
            // keeps the mention isolated from adjacent typing.
            const trailingText = $createTextNode(after.length > 0 ? after : ' ')
            mentionNode.insertAfter(trailingText)
            trailingText.select(0, 0)
        })
        close()
    }, [editor, close, mentionType])

    useEffect(() => {
        return editor.registerCommand(
            KEY_ESCAPE_COMMAND,
            () => {
                if (isOpen) {
                    close()
                    return true
                }
                return false
            },
            COMMAND_PRIORITY_HIGH,
        )
    }, [editor, isOpen, close])

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const result = getQueryAndRange(editor, trigger)
                if (result) {
                    matchRef.current = result
                    setQuery(result.query)
                    setIsOpen(true)
                    setTimeout(updateDropdownPosition, 0)
                } else if (isOpen) {
                    close()
                }
            })
        })
    }, [editor, trigger, isOpen, close, updateDropdownPosition])

    if (!isOpen) return null

    return (
        <MentionDropdown
            anchorRect={anchorRect}
            items={filteredItems}
            onSelect={handleSelect}
            onClose={close}
            emptyMessage={emptyMessage}
        />
    )
}
